/**
 * «Система → Состояние» (WP-17): one compact tile per subsystem — a badge (colour + word), one
 * line of detail and the tab where it is fixed. A check in the danger tone is «failed»: it also
 * shows as a banner on every tab of the page, so a broken backup or an unreadable key vault is
 * impossible to miss from «Оплата» or «Ключи провайдеров». Pure: the page passes the answers
 * of the requests it already makes.
 */
import type { BackupStatus } from '@/components/BackupsCard'
import type { McpStatus } from '@/components/McpCard'
import type { ProviderBalance } from '@/components/ProviderBalancesCard'
import { STATE_VIEW } from '@/lib/backups'
import { fmtInt } from '@/lib/format'
import type { SecretsOverview } from '@/lib/secrets'

export type SystemTab = 'state' | 'payments' | 'secrets' | 'models' | 'keys' | 'appearance'

export interface ProviderKeyRow {
  id: number
  provider: string
  provider_title?: string
  label: string
  key_last4: string | null
  weight: number
  is_healthy: boolean
  is_enabled: boolean
  last_error: string | null
  uses: number
  benched_for_sec: number | null
  source: 'env' | 'db'
}

export interface PayProviderRow {
  name: string
  configured: boolean
  enabled: boolean
  flag_key: string
  note: string
}

export type CheckTone = 'ok' | 'warn' | 'danger' | 'neutral'

export interface SystemCheck {
  id: 'backup' | 'balances' | 'payments' | 'keys' | 'mcp' | 'secrets'
  title: string
  tone: CheckTone
  /** the badge word */
  badge: string
  /** one line: what is wrong or what is fine */
  detail: string
  /** where it is fixed */
  tab: SystemTab
  /** the link's words */
  linkLabel: string
}

export const PAY_LABEL: Record<string, string> = { heleket: 'Heleket (крипта)', stars: 'Telegram Stars', sbp: 'СБП' }

export interface HealthInput {
  backup?: BackupStatus
  balances?: ProviderBalance[]
  payProviders?: PayProviderRow[]
  keys?: ProviderKeyRow[]
  mcp?: McpStatus
  /** owner only; undefined for other roles */
  secrets?: SecretsOverview
}

/** The checks whose answers have arrived, in the strip's order. */
export function systemChecks(h: HealthInput): SystemCheck[] {
  const out: SystemCheck[] = []
  if (h.backup) {
    const v = STATE_VIEW[h.backup.state]
    out.push({
      id: 'backup',
      title: 'Бэкап базы',
      tone: v.tone,
      badge: v.label,
      detail: v.hint,
      tab: 'state',
      linkLabel: 'Открыть бэкапы',
    })
  }
  if (h.balances) {
    const live = h.balances.filter((b) => b.configured && b.enabled)
    const down = live.filter((b) => b.error)
    out.push({
      id: 'balances',
      title: 'AI-провайдеры',
      tone: down.length > 0 ? 'warn' : live.length > 0 ? 'ok' : 'neutral',
      badge: down.length > 0 ? 'Недоступны' : live.length > 0 ? 'Подключены' : 'Не настроены',
      detail:
        down.length > 0
          ? `Не отвечают: ${down.map((b) => b.title).join(', ')}. Проверьте ключи.`
          : `Подключено ${fmtInt(live.length)} из ${fmtInt(h.balances.length)}.`,
      tab: 'state',
      linkLabel: 'Открыть балансы',
    })
  }
  if (h.payProviders) {
    const broken = h.payProviders.filter((p) => p.enabled && !p.configured)
    const on = h.payProviders.filter((p) => p.enabled && p.configured)
    out.push({
      id: 'payments',
      title: 'Оплата',
      tone: broken.length > 0 ? 'danger' : on.length > 0 ? 'ok' : 'warn',
      badge: broken.length > 0 ? 'Включена без ключей' : on.length > 0 ? 'Принимает' : 'Всё выключено',
      detail:
        broken.length > 0
          ? `Включены без ключей: ${broken.map((p) => PAY_LABEL[p.name] ?? p.name).join(', ')} — оплата этим способом не пройдёт.`
          : `Включено ${fmtInt(on.length)} из ${fmtInt(h.payProviders.length)}.`,
      tab: 'payments',
      linkLabel: 'Открыть «Оплату»',
    })
  }
  if (h.keys) {
    const enabled = h.keys.filter((k) => k.is_enabled)
    const benched = enabled.filter((k) => !k.is_healthy)
    out.push({
      id: 'keys',
      title: 'Ключи провайдеров',
      tone: enabled.length > 0 && benched.length === enabled.length ? 'danger' : benched.length > 0 ? 'warn' : enabled.length > 0 ? 'ok' : 'neutral',
      badge:
        enabled.length > 0 && benched.length === enabled.length
          ? 'Все сняты'
          : benched.length > 0
            ? 'Есть снятые'
            : enabled.length > 0
              ? 'В ротации'
              : 'Нет ключей',
      detail:
        benched.length > 0
          ? `Снято с ротации после ошибки: ${fmtInt(benched.length)} из ${fmtInt(enabled.length)}.`
          : `Включено ${fmtInt(enabled.length)} из ${fmtInt(h.keys.length)}, все отвечают.`,
      tab: 'keys',
      linkLabel: 'Открыть ключи провайдеров',
    })
  }
  if (h.mcp) {
    out.push({
      id: 'mcp',
      title: 'Higgsfield MCP',
      tone: h.mcp.last_error ? 'warn' : h.mcp.connected ? 'ok' : 'neutral',
      badge: h.mcp.last_error ? 'Ошибка' : h.mcp.connected ? 'Подключён' : 'Не подключён',
      detail: h.mcp.last_error
        ? h.mcp.last_error
        : h.mcp.connected
          ? `Инструментов: ${fmtInt(h.mcp.tools?.length ?? 0)}.`
          : 'MCP-модели скрыты из каталога.',
      tab: 'models',
      linkLabel: 'Открыть «Модели и маршруты»',
    })
  }
  if (h.secrets) {
    const unavailable = h.secrets.vault_available === false
    const broken = !unavailable && !h.secrets.vault_ok
    out.push({
      id: 'secrets',
      title: 'Ключи интеграций',
      tone: unavailable || broken ? 'danger' : h.secrets.editable ? 'ok' : 'neutral',
      badge: unavailable ? 'Хранилище недоступно' : broken ? 'Не расшифровываются' : h.secrets.editable ? 'В порядке' : 'Только просмотр',
      detail: unavailable
        ? 'Действуют значения из .env; сохранённые в админке ключи сейчас не прочитать.'
        : broken
          ? 'Часть сохранённых ключей не расшифровывается — для них действуют значения из .env.'
          : h.secrets.editable
            ? 'Ключи хранятся зашифрованными, правка — с кодом 2FA.'
            : 'Правка из админки выключена (ADMIN_SECRETS_ENABLED=false).',
      tab: 'secrets',
      linkLabel: 'Открыть ключи интеграций',
    })
  }
  return out
}

/** Failed checks (danger): the banner on every tab. */
export function failedChecks(checks: readonly SystemCheck[]): SystemCheck[] {
  return checks.filter((c) => c.tone === 'danger')
}

/**
 * The rail's «Система» badge (WP-16, WP-17): the same failed checks as the page's banner, so a
 * problem is visible from any page — a backup that did not run or verify (`failed` is a warn
 * tile on the page but the rail has always flagged it), payments switched on without keys, every
 * provider key benched, an unreadable key vault. Words for the badge's screen-reader label, or
 * null when nothing failed.
 */
export function systemRailAlert(checks: readonly SystemCheck[], backup?: BackupStatus): string | null {
  const parts: string[] = []
  const st = backup?.state
  if (st === 'failed' || st === 'verify_failed') parts.push('бэкап не прошёл')
  else if (st === 'stale') parts.push('бэкап устарел')
  for (const c of failedChecks(checks)) {
    if (c.id === 'backup') {
      if (parts.length === 0) parts.push(c.badge.toLowerCase())
      continue
    }
    parts.push(`${c.title}: ${c.badge.toLowerCase()}`)
  }
  return parts.length > 0 ? parts.join(', ') : null
}
